import React, { lazy } from 'react'
import { BrowserRouter as Router, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { ResetCSS } from '@pancakeswap/uikit'
import BigNumber from 'bignumber.js'
import useUserAgent from 'hooks/useUserAgent'
import useScrollOnRouteChange from 'hooks/useScrollOnRouteChange'
import { DatePickerPortal } from 'components/DatePicker'
import GlobalStyle from './style/Global'
import Menu from './components/Menu'
import SuspenseWithChunkError from './components/SuspenseWithChunkError'
import { ToastListener } from './contexts/ToastsContext'
import PageLoader from './components/Loader/PageLoader'
import EasterEgg from './components/EasterEgg'
import { RedirectPathToSwapOnly } from './views/Swap/redirects'

// Views
import Pools from './views/Pools'
import Swap from './views/Swap'

// Lazy-loaded views
const Home = lazy(() => import('./views/Home'))
const Farms = lazy(() => import('./views/Farms'))
const NotFound = lazy(() => import('./views/NotFound'))
const AddLiquidity = lazy(() => import('./views/AddLiquidity'))
const Liquidity = lazy(() => import('./views/Pool'))
const PoolFinder = lazy(() => import('./views/PoolFinder'))
const RemoveLiquidity = lazy(() => import('./views/RemoveLiquidity'))
const Charts = lazy(() => import('./views/Charts'))

BigNumber.config({
  EXPONENTIAL_AT: 1000,
  DECIMAL_PLACES: 80,
})

const App: React.FC = () => {
  useScrollOnRouteChange()
  useUserAgent()

  return (
    <Router>
      <ResetCSS />
      <GlobalStyle />
      <Menu>
        <SuspenseWithChunkError fallback={<PageLoader />}>
          <AppRoutes />
        </SuspenseWithChunkError>
      </Menu>
      <EasterEgg iterations={2} />
      <ToastListener />
      <DatePickerPortal />
    </Router>
  )
}

const AppRoutes: React.FC = () => {
  const location = useLocation()

  return (
    <Switch location={location}>
      {/* Main Routes */}
      <Route path="/" exact>
        <Home />
      </Route>
      <Route path="/farms" exact>
        <Farms />
      </Route>
      <Route path="/pools" exact>
        <Pools />
      </Route>

      <Route path="/info" render={() => <Charts key={`charts-${location.key}`} />} />

      {/* Swap and Liquidity Routes */}
      <Route exact strict path="/swap" render={() => <Swap key={`swap-${location.key}`} />} />
      <Route exact strict path="/swap/:outputCurrency" component={RedirectPathToSwapOnly} />
      <Route exact strict path="/find" component={PoolFinder} />
      <Route exact strict path="/liquidity" component={Liquidity} />
      <Route exact path="/add" component={AddLiquidity} />
      <Route exact path="/create" component={AddLiquidity} />
      <Route exact strict path="/remove" component={RemoveLiquidity} />

      {/* Legacy Redirects */}
      <Route path="/pool">
        <Redirect to="/liquidity" />
      </Route>
      <Route path="/staking">
        <Redirect to="/pools" />
      </Route>
      <Route path="/syrup">
        <Redirect to="/pools" />
      </Route>
      {/* 404 Route */}
      <Route component={NotFound} />
    </Switch>
  )
}

export default React.memo(App)
